import React from "react";
import TrendingNews, { TrendingNewsProps } from "./TrendingNews";
import { getNewsData } from "../../../services/TradingMallService";
import type { INewsItem } from "../../../interfaces/News.interface";

/**
 * AEM-authored properties for TrendingNews component
 * These properties are configured in the AEM dialog
 */
export interface TrendingNewsAEMProps {
  /**
   * The heading/title of the trending news section
   * @default "Trending"
   */
  title?: string;
  
  /**
   * Subtitle/description text displayed below the title
   * @default "Get real time updates about trending stocks"
   */
  subtitle?: string;
  
  /**
   * Maximum number of articles to display
   * @default 10
   */
  maxArticles?: number | string;
  
  /**
   * Category tags to filter trending news
   * Can be a comma-separated string or array of strings
   * Example: "Technology,Finance" or ["Technology", "Finance"]
   */
  categoryTags?: string | string[];
  
  /**
   * Whether to show "View All" link
   * @default false
   */
  showViewAll?: boolean | string;
  
  /**
   * Text for "View All" link
   * @default "View all"
   */
  viewAllText?: string;
  
  /**
   * URL for "View All" link
   */
  viewAllLink?: string;
  
  /**
   * Error title text
   * @default "Unable to fetch the information at this time"
   */
  errorTitle?: string;
  
  /**
   * Error description text
   * @default "Please try again later"
   */
  errorDescription?: string;
  
  /**
   * Retry button text
   * @default "Retry"
   */
  errorRetryButtonText?: string;
  
  /**
   * Error image source URL
   */
  errorImageSrc?: string;
  
  /**
   * Error image alt text
   * @default "Error"
   */
  errorImageAlt?: string;
  
  /**
   * Empty state title
   * @default "No results found"
   */
  emptyStateTitle?: string;
  
  /**
   * Empty state description
   * @default "No trending news available at the moment."
   */
  emptyStateDescription?: string;
  
  /**
   * Empty state image source URL
   */
  emptyStateImage?: string;
  
  /**
   * Aria label for left scroll button
   * @default "Scroll trending news left"
   */
  scrollLeftAriaLabel?: string;
  
  /**
   * Aria label for right scroll button
   * @default "Scroll trending news right"
   */
  scrollRightAriaLabel?: string;
  
  /**
   * Timestamp labels for formatting
   */
  timestampLabels?: {
    justNow?: string;
    minutesAgo?: string;
    hoursAgo?: string;
    daysAgo?: string;
  };
  
  /**
   * Images configuration
   */
  images?: {
    defaultSourceLogo?: string;
    defaultNewsBanner?: string;
    errorImage?: string;
    noResultsImage?: string;
  };
  
  /**
   * AEM-specific: Indicates if component is empty (for EditConfig)
   * This is set by AEM when component is dragged but not configured
   */
  isEmpty?: boolean;
}

const normalizeFilters = (categoryTags?: string | string[]): string[] => {
  if (!categoryTags) {
    return [];
  }

  if (typeof categoryTags === "string") {
    return categoryTags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return categoryTags.map((tag) => tag.trim()).filter(Boolean);
};

/**
 * AEM Wrapper Component for TrendingNews
 *
 * This component:
 * - Receives AEM-authored properties from the dialog
 * - Fetches trending news on the server
 * - Handles error/empty states
 * - Passes data to the base TrendingNews component
 *
 * Usage in AEM:
 * - Component resource type: 'your-app/components/trendingnews'
 * - Authors configure: title, maxArticles, categoryTags, etc. via dialog
 */
const TrendingNewsAEM = async (props: TrendingNewsAEMProps) => {
  const {
    title,
    subtitle,
    maxArticles = 10,
    categoryTags,
    showViewAll = false,
    viewAllText,
    viewAllLink,
    errorTitle,
    errorDescription,
    errorRetryButtonText,
    errorImageSrc,
    errorImageAlt,
    emptyStateTitle,
    emptyStateDescription,
    emptyStateImage,
    scrollLeftAriaLabel,
    scrollRightAriaLabel,
    timestampLabels,
    images,
    isEmpty = false,
  } = props;

  // Normalize maxArticles to number
  const maxItems =
    typeof maxArticles === "string"
      ? parseInt(maxArticles, 10) || 10
    : maxArticles || 10;

  // Normalize showViewAll to boolean
  const shouldShowViewAll =
    typeof showViewAll === "string"
      ? showViewAll.toLowerCase() === "true"
    : showViewAll || false;

  // EditConfig: Show placeholder when component is empty
  if (isEmpty) {
    return (
      <div className="px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg">
        <div className="text-center py-8">
          <p className="text-gray-500 text-sm font-medium mb-2">
            Trending News Component
          </p>
          <p className="text-gray-400 text-xs">
            Configure this component in the AEM dialog
          </p>
        </div>
      </div>
    );
  }

  const filters = normalizeFilters(categoryTags);
  let trendingNews: INewsItem[] = [];
  let hasError = false;

  try {
    const newsData = await getNewsData({
      newsType: "trending",
      filters,
      page: 1,
      limit: maxItems,
    });
    trendingNews = Array.isArray(newsData) ? newsData : [];
  } catch (error) {
    hasError = true;
    trendingNews = [];
  }

  // Prepare props for base component
  const trendingNewsProps: TrendingNewsProps = {
    trendingNews,
    title: title ?? "Trending",
    subtitle: subtitle ?? "Get real time updates about trending stocks",
    maxItems,
    showViewAll: shouldShowViewAll,
    viewAllText: viewAllText ?? "View all",
    viewAllLink: viewAllLink ?? "",
    hasError,
    errorTitle: errorTitle ?? "Unable to fetch the information at this time",
    errorDescription: errorDescription ?? "Please try again later",
    errorRetryButtonText: errorRetryButtonText || "Retry",
    errorImageAlt: errorImageAlt || "Error",
    errorImageSrc: errorImageSrc || images?.errorImage || "",
    emptyStateTitle: emptyStateTitle || "No results found",
    emptyStateDescription: emptyStateDescription || "No trending news available at the moment.",
    emptyStateImage: emptyStateImage || images?.noResultsImage,
    scrollLeftAriaLabel: scrollLeftAriaLabel || "Scroll trending news left",
    scrollRightAriaLabel: scrollRightAriaLabel || "Scroll trending news right",
    timestampLabels,
    images,
  };

  return <TrendingNews {...trendingNewsProps} />;
};

/**
 * EditConfig for AEM Authoring
 *
 * This configuration tells AEM when to show the placeholder.
 * The component is considered "empty" when:
 * - No title is provided, OR
 * - maxArticles is not set
 */
export const TrendingNewsEditConfig = {
  isEmpty: (props: TrendingNewsAEMProps): boolean => {
    // Component is empty if no title is provided
    // You can customize this logic based on your requirements
    return !props.title || props.isEmpty === true;
  },

  // Resource type for AEM mapping
  // This should match your AEM component resource type
  resourceType: "your-app/components/trendingnews",
};

export default TrendingNewsAEM;

